import { useState } from 'react';
import axios from 'axios';
import {
  ShieldCheck, Play, ArrowClockwise, CheckCircle, XCircle, Warning, Broom,
  Cloud, HardDrives, Copy, MagnifyingGlassMinus, Trash,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { formatDateTime } from '@/utils/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APP_LABELS = {
  shizai: '資材',
  mfg: '製造',
  knowledge: 'ナレッジ',
  it: 'IT',
};

const BACKEND_META = {
  emergent: { label: 'Emergent', Icon: Cloud, klass: 'text-sky-600' },
  nas:      { label: 'NAS',      Icon: HardDrives, klass: 'text-amber-600' },
};

/**
 * 整合性チェック — Storage integrity verifier.
 *
 * Walks every FILE_BUCKETS row and HEADs the storage backend to confirm the
 * file physically exists. Flags:
 *   - missing : DB row exists, HEAD returned 404 → file was deleted
 *               out-of-band (SMB delete, cloud purge, etc.)
 *   - error   : HEAD raised (unreachable NAS, config problem, etc.)
 *
 * Admin can then batch-mark broken rows so downstream views can hide them.
 */
export function VerifyIntegrityCard() {
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const [showOnlyIssues, setShowOnlyIssues] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [marking, setMarking] = useState(false);

  // Reverse-orphan scan state (independent from forward scan above)
  const [reverseRunning, setReverseRunning] = useState(false);
  const [reverseReport, setReverseReport] = useState(null);
  const [reverseSelected, setReverseSelected] = useState(new Set());
  const [reverseDeleting, setReverseDeleting] = useState(false);

  const runScan = async () => {
    setRunning(true);
    setSelected(new Set());
    try {
      const t0 = performance.now();
      const r = await axios.post(`${API}/settings/storage/verify`, {}, { withCredentials: true });
      const ms = Math.round(performance.now() - t0);
      setReport({ ...r.data, elapsed_ms: ms });
      if (r.data.missing + r.data.errored === 0) {
        toast.success(`整合性 OK: ${r.data.scanned} 件すべて確認 (${ms}ms)`);
      } else {
        toast.warning(`${r.data.missing + r.data.errored} 件に問題があります`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || '整合性チェックに失敗しました');
    } finally {
      setRunning(false);
    }
  };

  const toggleRow = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const rowKey = (r) => `${r.collection}::${r.row_id || r.mongo_id}`;

  const toggleAllVisible = () => {
    if (!report) return;
    const visible = (report.rows || []).filter((r) => (showOnlyIssues ? true : true)).map(rowKey);
    const allSelected = visible.every((k) => selected.has(k));
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(visible));
    }
  };

  const markSelectedBroken = async () => {
    if (!report || selected.size === 0) return;
    if (!window.confirm(`${selected.size} 件を「破損」としてマークします。DB 行は残り、ファイルポインタも触りません。よろしいですか？`)) return;
    const items = (report.rows || [])
      .filter((r) => selected.has(rowKey(r)))
      .map((r) => ({ collection: r.collection, row_id: r.row_id, mongo_id: r.mongo_id }));
    setMarking(true);
    try {
      const r = await axios.post(`${API}/settings/storage/verify/mark-broken`, { items }, { withCredentials: true });
      toast.success(`${r.data.marked} 件を破損としてマークしました`);
      // Refresh scan so the `broken_at` column shows the new timestamps
      await runScan();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'マークに失敗しました');
    } finally {
      setMarking(false);
    }
  };

  const copyPath = async (path) => {
    try { await navigator.clipboard.writeText(path); toast.success('パスをコピーしました'); }
    catch { toast.error('コピーに失敗しました'); }
  };

  // ── Reverse-orphan scan handlers ────────────────────────────────────
  const runReverseScan = async () => {
    setReverseRunning(true);
    setReverseSelected(new Set());
    try {
      const t0 = performance.now();
      const r = await axios.post(`${API}/settings/storage/verify/reverse-orphans`, {}, { withCredentials: true });
      const ms = Math.round(performance.now() - t0);
      setReverseReport({ ...r.data, elapsed_ms: ms });
      if ((r.data.orphans_total || 0) === 0) {
        toast.success(`ストレージに逆孤立ファイルはありません (${ms}ms)`);
      } else {
        toast.warning(`${r.data.orphans_total} 件の逆孤立ファイルを検出しました`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || '逆方向スキャンに失敗しました');
    } finally {
      setReverseRunning(false);
    }
  };

  const reverseKey = (o) => `${o.backend}::${o.path}`;

  const toggleReverseRow = (key) => {
    setReverseSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Backends that don't support DELETE (Emergent) — checkbox is disabled
  // for those rows so admins can't accidentally submit doomed selections.
  const deletableBackends = new Set(
    (reverseReport?.backends || [])
      .filter((b) => b.delete_supported !== false)
      .map((b) => b.backend),
  );

  const toggleAllReverse = () => {
    if (!reverseReport) return;
    const keys = (reverseReport.orphans || [])
      .filter((o) => deletableBackends.has(o.backend))
      .map(reverseKey);
    const allSelected = keys.length > 0 && keys.every((k) => reverseSelected.has(k));
    setReverseSelected(allSelected ? new Set() : new Set(keys));
  };

  const deleteSelectedOrphans = async () => {
    if (!reverseReport || reverseSelected.size === 0) return;
    if (!window.confirm(`ストレージから ${reverseSelected.size} 件のファイルを完全に削除します。\n\nこの操作は元に戻せません。よろしいですか？`)) return;
    const items = (reverseReport.orphans || [])
      .filter((o) => reverseSelected.has(reverseKey(o)))
      .map((o) => ({ backend: o.backend, path: o.path }));
    setReverseDeleting(true);
    try {
      const r = await axios.post(`${API}/settings/storage/verify/delete-orphans`, { items }, { withCredentials: true });
      const errCount = (r.data.errors || []).length;
      if (errCount > 0) {
        toast.warning(`${r.data.deleted} 件削除しました（${errCount} 件はスキップ）`);
      } else {
        toast.success(`${r.data.deleted} 件のファイルを削除しました`);
      }
      // Refresh so the deleted items disappear from the list
      await runReverseScan();
    } catch (e) {
      toast.error(e.response?.data?.detail || '削除に失敗しました');
    } finally {
      setReverseDeleting(false);
    }
  };

  const visibleRows = report
    ? (showOnlyIssues ? (report.rows || []) : (report.rows || []))
    : [];

  return (
    <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden" data-testid="storage-verify-card">
      <div className="px-5 py-4 border-b border-zinc-200 flex items-start gap-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-zinc-50 text-emerald-700 shrink-0">
          <ShieldCheck size={20} weight="duotone" />
        </span>
        <div className="flex-1 min-w-0">
          <h2 className="font-bold text-zinc-900">整合性チェック</h2>
          <p className="text-[12px] text-zinc-500 mt-0.5 leading-relaxed">
            DB 上のファイル参照と、実際のストレージ上のファイルを突き合わせます。
            <b>孤立ポインタ</b>（DB に行はあるがファイルが無い）を検出して安全に整理できます。
          </p>
        </div>
        <Button
          onClick={runScan}
          disabled={running}
          data-testid="storage-verify-run"
          className="bg-teal-700 hover:bg-teal-800 text-white shrink-0"
        >
          {running ? (
            <><ArrowClockwise size={14} className="mr-1 animate-spin" />スキャン中…</>
          ) : (
            <><Play size={14} className="mr-1" />スキャン実行</>
          )}
        </Button>
      </div>

      {!report && !running && (
        <div className="px-5 py-6 text-[13px] text-zinc-400 flex items-center gap-2" data-testid="storage-verify-empty">
          <ShieldCheck size={16} weight="duotone" />
          「スキャン実行」で全アプリのファイル整合性を確認します（数秒）。
        </div>
      )}

      {report && (
        <>
          {/* Summary row */}
          <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-50 flex flex-wrap items-center gap-4 text-[12px]" data-testid="storage-verify-summary">
            <div className="flex items-center gap-1.5">
              <span className="text-zinc-500">スキャン:</span>
              <b className="text-zinc-900 tabular-nums">{report.scanned.toLocaleString()}</b>
            </div>
            <div className="flex items-center gap-1.5 text-emerald-700">
              <CheckCircle size={13} weight="duotone" />
              <span>正常:</span>
              <b className="tabular-nums">{report.ok.toLocaleString()}</b>
            </div>
            {report.missing > 0 && (
              <div className="flex items-center gap-1.5 text-rose-700" data-testid="storage-verify-missing-count">
                <XCircle size={13} weight="duotone" />
                <span>ファイル無し:</span>
                <b className="tabular-nums">{report.missing.toLocaleString()}</b>
              </div>
            )}
            {report.errored > 0 && (
              <div className="flex items-center gap-1.5 text-amber-700" data-testid="storage-verify-error-count">
                <Warning size={13} weight="duotone" />
                <span>エラー:</span>
                <b className="tabular-nums">{report.errored.toLocaleString()}</b>
              </div>
            )}
            <div className="ml-auto text-zinc-400 text-[11px]">
              {report.elapsed_ms}ms
            </div>
          </div>

          {/* Per-app breakdown */}
          <div className="px-5 py-3 border-b border-zinc-200 flex flex-wrap gap-2 text-[11px]" data-testid="storage-verify-by-app">
            {Object.entries(report.by_app || {}).map(([app, c]) => {
              const badBad = (c.missing || 0) + (c.errored || 0);
              return (
                <span
                  key={app}
                  className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border ${
                    badBad > 0
                      ? 'bg-rose-50 border-rose-200 text-rose-800'
                      : 'bg-emerald-50 border-emerald-200 text-emerald-700'
                  }`}
                >
                  <b>{APP_LABELS[app] || app}</b>
                  <span className="tabular-nums">
                    {c.ok}/{c.scanned}
                  </span>
                  {badBad > 0 && (
                    <span className="tabular-nums">· 問題 {badBad}</span>
                  )}
                </span>
              );
            })}
          </div>

          {/* Actions row when there are issues */}
          {(report.missing + report.errored) > 0 && (
            <div className="px-5 py-2.5 border-b border-zinc-200 bg-amber-50/40 flex items-center gap-3 text-[12px]">
              <label className="inline-flex items-center gap-1.5 cursor-pointer">
                <Checkbox
                  checked={showOnlyIssues}
                  onCheckedChange={(v) => setShowOnlyIssues(!!v)}
                  data-testid="storage-verify-filter-issues"
                />
                <span className="text-zinc-700">問題のみ表示</span>
              </label>
              <Button
                size="sm"
                variant="outline"
                onClick={toggleAllVisible}
                className="h-7 text-[11px]"
                data-testid="storage-verify-select-all"
              >
                {(visibleRows.length > 0 && visibleRows.every((r) => selected.has(rowKey(r))))
                  ? 'すべて解除'
                  : 'すべて選択'}
              </Button>
              <div className="ml-auto flex items-center gap-2">
                <span className="text-zinc-500">選択 <b className="text-zinc-800 tabular-nums">{selected.size}</b> 件</span>
                <Button
                  size="sm"
                  onClick={markSelectedBroken}
                  disabled={selected.size === 0 || marking}
                  data-testid="storage-verify-mark-broken"
                  className="h-7 text-[11px] bg-rose-600 hover:bg-rose-700 text-white"
                >
                  {marking ? (
                    <><ArrowClockwise size={11} className="mr-1 animate-spin" />処理中…</>
                  ) : (
                    <><Broom size={11} className="mr-1" />破損としてマーク</>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Results table */}
          {visibleRows.length === 0 ? (
            <div className="px-5 py-6 text-[13px] flex items-center gap-2 text-emerald-700" data-testid="storage-verify-all-ok">
              <CheckCircle size={16} weight="duotone" />
              すべてのファイルが確認できました（{report.scanned} 件）。
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
              <table className="w-full text-[12px]" data-testid="storage-verify-results-table">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="text-left text-zinc-500 border-b border-zinc-200 bg-zinc-50">
                    <th className="py-2 pl-5 pr-2 w-8"> </th>
                    <th className="py-2 pr-3">状態</th>
                    <th className="py-2 pr-3">アプリ</th>
                    <th className="py-2 pr-3">バックエンド</th>
                    <th className="py-2 pr-3">ファイル名</th>
                    <th className="py-2 pr-3">エラー</th>
                    <th className="py-2 pr-5"> </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((r) => {
                    const key = rowKey(r);
                    const Backend = BACKEND_META[r.storage_backend] || BACKEND_META.emergent;
                    return (
                      <tr
                        key={key}
                        className={`border-b border-zinc-200 last:border-b-0 hover:bg-zinc-50 ${
                          r.broken_at ? 'opacity-60' : ''
                        }`}
                        data-testid={`storage-verify-row-${key}`}
                      >
                        <td className="py-2 pl-5 pr-2">
                          <Checkbox
                            checked={selected.has(key)}
                            onCheckedChange={() => toggleRow(key)}
                            data-testid={`storage-verify-select-${key}`}
                          />
                        </td>
                        <td className="py-2 pr-3 whitespace-nowrap">
                          {r.status === 'missing' ? (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-100 text-rose-700 border border-rose-200">
                              <XCircle size={10} weight="bold" />
                              ファイル無し
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200">
                              <Warning size={10} weight="bold" />
                              エラー
                            </span>
                          )}
                          {r.broken_at && (
                            <span
                              className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-zinc-200 text-zinc-600"
                              title={`マーク済み ${r.broken_at}`}
                            >
                              <Broom size={9} weight="bold" />
                              マーク済み
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-zinc-700">{APP_LABELS[r.app] || r.app}</td>
                        <td className="py-2 pr-3">
                          <span className={`inline-flex items-center gap-1 ${Backend.klass}`}>
                            <Backend.Icon size={12} weight="duotone" />
                            {Backend.label}
                          </span>
                        </td>
                        <td className="py-2 pr-3 font-mono text-[11px] text-zinc-700 max-w-[260px] truncate" title={r.storage_path}>
                          {r.filename || r.storage_path}
                        </td>
                        <td className="py-2 pr-3 text-[11px] text-zinc-500 max-w-[260px] truncate" title={r.error || ''}>
                          {r.error || '—'}
                        </td>
                        <td className="py-2 pr-5 text-right">
                          <button
                            onClick={() => copyPath(r.storage_path)}
                            title="パスをコピー"
                            data-testid={`storage-verify-copy-${key}`}
                            className="inline-flex items-center justify-center w-6 h-6 rounded text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800"
                          >
                            <Copy size={12} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── Reverse-orphan scan section ─────────────────────────────── */}
      <div className="border-t-4 border-zinc-100" data-testid="storage-reverse-orphan-section">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-start gap-3">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-zinc-50 text-rose-700 shrink-0">
            <MagnifyingGlassMinus size={20} weight="duotone" />
          </span>
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-zinc-900">逆方向スキャン（Reverse-Orphan）</h2>
            <p className="text-[12px] text-zinc-500 mt-0.5 leading-relaxed">
              ストレージ上には存在するが <b>DB に参照が無いファイル</b> を検出します。
              古いアップロード、削除された行の残骸、テストデータなど — 静かに容量を食っているファイルを一括削除できます。
            </p>
          </div>
          <Button
            onClick={runReverseScan}
            disabled={reverseRunning}
            data-testid="storage-reverse-run"
            className="bg-rose-600 hover:bg-rose-700 text-white shrink-0"
          >
            {reverseRunning ? (
              <><ArrowClockwise size={14} className="mr-1 animate-spin" />スキャン中…</>
            ) : (
              <><Play size={14} className="mr-1" />逆方向スキャン</>
            )}
          </Button>
        </div>

        {!reverseReport && !reverseRunning && (
          <div className="px-5 py-6 text-[13px] text-zinc-400 flex items-center gap-2" data-testid="storage-reverse-empty">
            <MagnifyingGlassMinus size={16} weight="duotone" />
            「逆方向スキャン」で DB に参照の無いストレージファイルを検出します。
          </div>
        )}

        {reverseReport && (
          <>
            <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-50 flex flex-wrap items-center gap-3 text-[12px]" data-testid="storage-reverse-summary">
              {(reverseReport.backends || []).map((b) => {
                const Backend = BACKEND_META[b.backend] || BACKEND_META.emergent;
                const hasErr = b.error || b.unsupported;
                return (
                  <span
                    key={b.backend}
                    className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border ${
                      hasErr
                        ? 'bg-amber-50 border-amber-200 text-amber-800'
                        : b.orphans_count > 0
                          ? 'bg-rose-50 border-rose-200 text-rose-800'
                          : 'bg-emerald-50 border-emerald-200 text-emerald-700'
                    }`}
                    title={b.error || ''}
                  >
                    <Backend.Icon size={12} weight="duotone" className={Backend.klass} />
                    <b>{Backend.label}</b>
                    {hasErr ? (
                      <span className="text-[11px]">未対応/エラー</span>
                    ) : (
                      <>
                        <span className="tabular-nums">listed {b.listed}</span>
                        {b.orphans_count > 0 && (
                          <span className="tabular-nums">· 孤立 {b.orphans_count}</span>
                        )}
                        {b.is_truncated && (
                          <span className="text-[10px] font-bold px-1 rounded bg-amber-100 text-amber-700">切り捨て</span>
                        )}
                        {b.delete_supported === false && b.orphans_count > 0 && (
                          <span
                            className="text-[10px] font-bold px-1 rounded bg-zinc-200 text-zinc-700"
                            title="このバックエンドは削除 API を提供していません"
                            data-testid={`storage-reverse-nodelete-${b.backend}`}
                          >
                            削除不可
                          </span>
                        )}
                      </>
                    )}
                  </span>
                );
              })}
              <div className="ml-auto flex items-center gap-3">
                <span className="text-zinc-500">合計 <b className="text-zinc-900 tabular-nums">{reverseReport.orphans_total || 0}</b> 件</span>
                <span className="text-zinc-400 text-[11px]">{reverseReport.elapsed_ms}ms</span>
              </div>
            </div>

            {/* Persistent limitation banner for backends that don't support DELETE */}
            {(reverseReport.backends || []).some((b) => b.delete_supported === false && b.orphans_count > 0) && (
              <div
                className="px-5 py-2.5 border-b border-zinc-200 bg-amber-50 flex items-start gap-2 text-[12px] text-amber-900"
                data-testid="storage-reverse-emergent-nodelete-banner"
              >
                <Warning size={14} weight="duotone" className="text-amber-600 shrink-0 mt-0.5" />
                <span>
                  <b>Emergent クラウドストレージは削除 API を提供していません</b>（プラットフォーム仕様）。
                  Emergent 上の孤立ファイルは <b>確認のみ可能</b> で、この画面から削除することはできません。
                  必要であれば Emergent サポートに直接削除を依頼してください。
                </span>
              </div>
            )}

            {/* Per-backend error messages */}
            {(reverseReport.backends || []).filter((b) => b.error).map((b) => (
              <div
                key={`err-${b.backend}`}
                className="px-5 py-2.5 border-b border-zinc-200 bg-amber-50 flex items-start gap-2 text-[12px] text-amber-900"
                data-testid={`storage-reverse-error-${b.backend}`}
              >
                <Warning size={14} weight="duotone" className="text-amber-600 shrink-0 mt-0.5" />
                <span>
                  <b>{BACKEND_META[b.backend]?.label || b.backend}:</b> {b.error}
                </span>
              </div>
            ))}

            {(reverseReport.orphans_total || 0) > 0 && (
              <>
                <div className="px-5 py-2.5 border-b border-zinc-200 bg-rose-50/40 flex items-center gap-3 text-[12px]">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={toggleAllReverse}
                    className="h-7 text-[11px]"
                    data-testid="storage-reverse-select-all"
                  >
                    {reverseReport.orphans.every((o) => reverseSelected.has(reverseKey(o)))
                      ? 'すべて解除'
                      : 'すべて選択'}
                  </Button>
                  <div className="ml-auto flex items-center gap-2">
                    <span className="text-zinc-500">
                      選択 <b className="text-zinc-800 tabular-nums">{reverseSelected.size}</b> 件
                    </span>
                    <Button
                      size="sm"
                      onClick={deleteSelectedOrphans}
                      disabled={reverseSelected.size === 0 || reverseDeleting}
                      data-testid="storage-reverse-delete"
                      className="h-7 text-[11px] bg-rose-600 hover:bg-rose-700 text-white"
                    >
                      {reverseDeleting ? (
                        <><ArrowClockwise size={11} className="mr-1 animate-spin" />削除中…</>
                      ) : (
                        <><Trash size={11} className="mr-1" />選択を完全に削除</>
                      )}
                    </Button>
                  </div>
                </div>

                <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                  <table className="w-full text-[12px]" data-testid="storage-reverse-results-table">
                    <thead className="sticky top-0 bg-white z-10">
                      <tr className="text-left text-zinc-500 border-b border-zinc-200 bg-zinc-50">
                        <th className="py-2 pl-5 pr-2 w-8"> </th>
                        <th className="py-2 pr-3">バックエンド</th>
                        <th className="py-2 pr-3">ファイル名</th>
                        <th className="py-2 pr-3 text-right">サイズ</th>
                        <th className="py-2 pr-3">更新日時</th>
                        <th className="py-2 pr-5"> </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(reverseReport.orphans || []).map((o) => {
                        const key = reverseKey(o);
                        const Backend = BACKEND_META[o.backend] || BACKEND_META.emergent;
                        const canDelete = deletableBackends.has(o.backend);
                        return (
                          <tr
                            key={key}
                            className={`border-b border-zinc-200 last:border-b-0 hover:bg-zinc-50 ${canDelete ? '' : 'opacity-70'}`}
                            data-testid={`storage-reverse-row-${o.backend}-${o.path}`}
                          >
                            <td className="py-2 pl-5 pr-2">
                              <Checkbox
                                checked={reverseSelected.has(key)}
                                onCheckedChange={() => toggleReverseRow(key)}
                                disabled={!canDelete}
                                data-testid={`storage-reverse-select-${o.backend}-${o.path}`}
                                title={canDelete ? '削除対象に追加' : 'このバックエンドは削除 API を提供していません'}
                              />
                            </td>
                            <td className="py-2 pr-3">
                              <span className={`inline-flex items-center gap-1 ${Backend.klass}`}>
                                <Backend.Icon size={12} weight="duotone" />
                                {Backend.label}
                              </span>
                              {!canDelete && (
                                <span className="ml-1 inline-flex items-center px-1 py-0.5 rounded text-[9px] font-bold bg-zinc-200 text-zinc-600" title="削除 API 非対応">
                                  削除不可
                                </span>
                              )}
                            </td>
                            <td className="py-2 pr-3 font-mono text-[11px] text-zinc-700 max-w-[360px] truncate" title={o.path}>
                              {o.path}
                            </td>
                            <td className="py-2 pr-3 text-right tabular-nums text-zinc-600">
                              {o.size != null ? (o.size >= 1024 ? `${(o.size / 1024).toFixed(1)} KB` : `${o.size} B`) : '—'}
                            </td>
                            <td className="py-2 pr-3 text-[11px] text-zinc-500">
                              {o.last_modified ? formatDateTime(o.last_modified) : '—'}
                            </td>
                            <td className="py-2 pr-5 text-right">
                              <button
                                onClick={() => copyPath(o.path)}
                                title="パスをコピー"
                                data-testid={`storage-reverse-copy-${o.backend}-${o.path}`}
                                className="inline-flex items-center justify-center w-6 h-6 rounded text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800"
                              >
                                <Copy size={12} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {(reverseReport.orphans_total || 0) === 0 && (
              <div className="px-5 py-6 text-[13px] flex items-center gap-2 text-emerald-700" data-testid="storage-reverse-all-clean">
                <CheckCircle size={16} weight="duotone" />
                すべてのストレージファイルに DB 参照が確認できました（余計なファイルはありません）。
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
